<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ filename || '附件預覽' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="downloadFile">
          <v-icon>mdi-download</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="height: calc(100vh - 64px); position: relative">
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <div v-if="!loading && error" class="pa-4">
          <v-alert type="error">
            {{ error }}
          </v-alert>
        </div>

        <!-- PDF Preview -->
        <div v-else-if="!loading && isPdf && previewUrl" style="height: 100%">
          <PdfViewer :source="previewUrl" loading-text="載入中..." />
        </div>

        <!-- Image Preview -->
        <div
          v-else-if="!loading && isImage && previewUrl"
          class="d-flex align-center justify-center"
          style="height: 100%; background-color: #000"
        >
          <v-img :src="previewUrl" contain max-height="100%" max-width="100%" />
        </div>

        <!-- Other file types -->
        <div v-else-if="!loading && previewUrl" class="pa-4">
          <v-alert type="info"> 此文件類型無法預覽，請下載後查看 </v-alert>
          <v-btn color="primary" @click="downloadFile">
            <v-icon left>mdi-download</v-icon>
            下載文件
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import PdfViewer from '@/components/PdfViewer/PdfViewer';
import { attnDownloadHref } from '@/services/filters';

export default {
  name: 'AttachmentPreviewDialog',
  components: {
    PdfViewer,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    attachmentId: {
      type: String,
      default: null,
    },
    filename: {
      type: String,
      default: '',
    },
    contentType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewUrl: null,
      blobUrl: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    downloadUrl() {
      return this.attachmentId ? attnDownloadHref(this.attachmentId) : null;
    },
    isPdf() {
      return this.contentType?.includes('pdf') || this.filename?.toLowerCase().endsWith('.pdf');
    },
    isImage() {
      return (
        this.contentType?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(this.filename)
      );
    },
  },
  watch: {
    dialog(val) {
      if (val && this.attachmentId) {
        this.loadAttachment();
      } else if (!val) {
        this.cleanup();
      }
    },
  },
  methods: {
    async loadAttachment() {
      if (!this.attachmentId) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await api.get(`v1/attachments/${this.attachmentId}/download`, {
          responseType: 'blob',
        });

        // Create blob URL for preview
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || this.contentType,
        });
        this.blobUrl = URL.createObjectURL(blob);
        this.previewUrl = this.blobUrl;
      } catch (e) {
        console.error('Failed to load attachment:', e);
        this.error = '載入附件失敗，請稍後重試';
      } finally {
        this.loading = false;
      }
    },
    downloadFile() {
      if (this.blobUrl) {
        const link = document.createElement('a');
        link.href = this.blobUrl;
        link.download = this.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback to direct download
        window.open(this.downloadUrl, '_blank');
      }
    },
    cleanup() {
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      this.previewUrl = null;
      this.error = null;
    },
    close() {
      this.dialog = false;
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style scoped>
.v-card__text {
  position: relative;
}
</style>
